// brain.ts — the AI client. The ONE billed AI call this app makes:
//   POST https://api.orioralabs.com/api/apps/oriora_lockbox/ask
// with the customer's Oriora session token. The server holds the prompt + bills the user's
// Oriora wallet (Managed AI / BYOK). Whole documents stay local; ONLY the matched passages
// (retrieved on-device by Rust FTS5) + the question are sent for this one call.
//
// CRUD, not agentic: text-in (scoped JSON of passages + question), text-out (answer + citations).
// No agent loop, no second call, no tool-use chain, no direct OpenAI/Anthropic SDK in the app.
//
// HARDEST guardrail (LEGAL refusal fence): Lockbox quotes/locates what documents SAY; it NEVER
// interprets the user's legal/insurance/financial position or advises ("should I sue / am I
// covered"). Advice-seeking questions are blocked BEFORE the LLM call (isAdviceQuestion) — the user
// gets a cited-fact-only redirect, never an opinion. The server prompt enforces the same fence as a
// second line of defence.

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { Passage, HeadlineFact } from "./store";

const SERVER = "https://api.orioralabs.com";
const SLUG = "oriora_lockbox";

export type BrainErrorCode =
  | "insufficient_funds"
  | "managed_ai_blocked"
  | "unauthenticated"
  | "unavailable"
  | "no_store"
  | "too_large";

export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

/** Thrown when a question asks for advice/interpretation — blocked BEFORE any AI call (the fence). */
export class AdviceBlockedError extends Error {
  constructor() {
    super(
      "Lockbox can quote and locate what your documents say — but it can't give legal, insurance or financial advice. Try asking what a document states, e.g. \"How much notice does my lease need?\"",
    );
    this.name = "AdviceBlockedError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/${SLUG}/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) {
        await sleep(attempt * 800);
        continue;
      }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402)
      throw new BrainError("insufficient_funds", "Add funds in your Oriora account to keep using AI.");
    if (res.status === 403)
      throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) {
        await sleep(attempt * 800);
        continue;
      }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

/** One citation the server returns. `title`/`page` are copied from a passage we sent. */
export interface AskCitation {
  doc_id: string;
  title: string;
  page: number;
  /** Short note on what that document contributed. */
  why: string;
}

export interface AskResult {
  answer: string;
  citations: AskCitation[];
  /** 0..1 — the model's grounding confidence (low ⇒ "not clearly in your documents"). */
  confidence: number;
}

/** A passage as sent to the brain — the ONLY document content that leaves the device. */
export interface PassageRef {
  doc_id: string;
  title: string;
  kind: string;
  page: number;
  snippet: string;
}

/**
 * The LEGAL refusal fence (pre-LLM). Returns true if the question is asking for advice /
 * interpretation of the user's own position rather than a fact stated in a document. These are
 * blocked BEFORE the AI call so the model is never invited to opine. Deliberately conservative:
 * it matches advice INTENT phrasings ("should I", "am I covered", "can I sue", "is it legal",
 * "what are my rights", "do I have to", "what should I do"), not factual questions that merely
 * contain a legal word ("what does my lease say about deposits" passes through).
 */
export function isAdviceQuestion(qRaw: string): boolean {
  const q = ` ${qRaw.toLowerCase().replace(/[^a-z0-9\s']/g, " ").replace(/\s+/g, " ").trim()} `;
  const ADVICE_PATTERNS: RegExp[] = [
    /\bshould i\b/, // should I sign / should I sue / should I cancel
    /\bwhat should i\b/,
    /\bcan i sue\b/,
    /\bshould i sue\b/,
    /\bdo i have a (case|claim)\b/,
    /\bam i (covered|liable|entitled|protected|insured|allowed)\b/,
    /\bare we (covered|liable|entitled|protected|insured)\b/,
    /\bdo i have to\b/,
    /\bcan i (get out of|break|cancel|refuse|withhold|sue|claim)\b/,
    /\bwhat (are|re) my (legal |contractual )?(rights|options|chances|obligations|liabilities)\b/,
    /\bis (it|this|that) (?:[a-z']+ )?(legally |lawfully )?(legal|illegal|enforceable|binding|fair|allowed|valid)\b/,
    // "does/will my policy cover X" (a specific incident) asks for a coverage DETERMINATION — advice.
    // "what does my policy cover" (no incident) asks the doc to list its coverage — factual, must pass.
    /(?<!\bwhat )\b(will|does|would|is) (my |the )?(insurance|policy|warranty|plan|coverage|it|this) (cover|covered|pay|reimburse)\b/,
    /\bcan they (legally|evict|charge|sue|force|make me)\b/,
    /\bwhat (do|should) i do\b/,
    /\bis my (claim|case) (valid|strong|good)\b/,
    /\b(advise|advice) me\b/,
    /\bwhat would you (do|recommend|advise|suggest)\b/,
    /\bwill i win\b/,
  ];
  return ADVICE_PATTERNS.some((re) => re.test(q));
}

/**
 * Ask the box a plain-English question. The ONE AI feature.
 *
 * Flow: the FENCE runs first (advice-seeking → AdviceBlockedError, no call). Then we send ONLY the
 * already-locally-matched passages + the question. The server answers using ONLY those passages and
 * cites the source document + page. Raw documents never leave the device.
 *
 * The endpoint + Bearer JWT: POST /api/apps/oriora_lockbox/ask. The server prompt (operator-mirrored)
 * is OBSERVATIONAL + cited-only — it quotes/locates, never advises or interprets the user's position.
 */
export async function askDocuments(
  question: string,
  passages: Passage[],
  headlineFacts: HeadlineFact[] = [],
): Promise<AskResult> {
  if (isAdviceQuestion(question)) throw new AdviceBlockedError();
  if (passages.length === 0) {
    // No local match — answer honestly without spending a call.
    return {
      answer:
        "I couldn't find that in your documents. Try different words, or add the document that would have it.",
      citations: [],
      confidence: 0,
    };
  }
  const refs: PassageRef[] = passages.map((p) => ({
    doc_id: p.doc_id,
    title: p.title,
    kind: p.kind,
    page: p.page,
    snippet: p.snippet,
  }));
  const sentIds = new Set(passages.map((p) => p.doc_id));

  // The pre-ranked derived-signal slice (on-device, deterministic). Only the short fact SENTENCES
  // go up — already ordered most-decision-relevant first — so the ONE narration is grounded in the
  // user's own figures + dates, not generic knowledge. Capped defensively.
  const facts = headlineFacts.slice(0, 8).map((f) => ({ text: f.text, tone: f.tone }));

  const r = await brainCall<Partial<AskResult>>("ask", {
    question,
    passages: refs,
    headline_facts: facts,
    now_ts: Math.floor(Date.now() / 1000),
  });

  // Hallucinated-citation guard: keep only citations whose doc_id we actually sent as a passage.
  const citations: AskCitation[] = Array.isArray(r.citations)
    ? r.citations
        .filter((c): c is AskCitation => !!c && typeof c.doc_id === "string" && sentIds.has(c.doc_id))
        .slice(0, 8)
    : [];

  return {
    answer: String(r.answer ?? "").trim(),
    citations,
    confidence: typeof r.confidence === "number" ? r.confidence : 0,
  };
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds a FAITHFUL backup locally (export_backup); these relay it to the user's OWN
// connected cloud store — their GitHub repo or Google Drive (connected once in the Oriora
// account). Oriora RETAINS nothing. Plain HTTP → works on iOS. Endpoints: the generic
// POST/GET /api/apps/oriora_lockbox/backup (shared server route).
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/oriora_lockbox/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN cloud
 *  store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora retains
 *  nothing (text relay only). Throws no_store when nothing is connected. Endpoint: the shared
 *  POST /api/apps/oriora_lockbox/store route (distinct from the whole-collection `backup` route). */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the OS browser —
// the in-app https handback is robust on iOS where the custom-scheme deep-link is fragile.
// Session returns via auth:deep-link (App.tsx + ipc.rs). The web callback must allowlist
// `oriora-lockbox` as a return target (server-side, operator task).
export const openWebLogin = () => invoke<void>("open_login_window");

// ─────────────────────────────────────────────────────────────────────────────
// SERVER PROMPT (operator must mirror this at oriora-server for /api/apps/oriora_lockbox/ask).
// Kept here as the source-of-truth contract. The server runs ONE grounded call over the passages.
// ─────────────────────────────────────────────────────────────────────────────
export const ASK_SYSTEM_PROMPT = `You are Oriora Lockbox's document assistant. You answer the user's question USING ONLY the document passages provided in this request. Each passage carries its source document title and an approximate page.

You are ALSO given "headline_facts": a list of short, already-ranked statements that Lockbox derived deterministically ON THE USER'S DEVICE from their documents' figures, periods, rates and dates (e.g. "This lease states a deposit of £1,500.", "This policy expires on 1 Sep 2026 (in 23 days).", "This document states a representative APR of 29.9%."), each tagged with a salience tone (urgent | notable | neutral). These are TRUE, pre-computed observations — use them to make your answer specific and grounded, and to surface the single most decision-relevant figure or date when the question invites it. They are facts to RESTATE, never to interpret.

ABSOLUTE RULES:
1. ANSWER ONLY FROM THE PASSAGES AND HEADLINE FACTS. Never use outside knowledge. If neither contains the answer, say plainly: "I couldn't find that in your documents." Do not guess.
2. QUOTE AND LOCATE — DO NOT ADVISE. You may state what a document SAYS and where ("Your tenancy agreement (page 2) says two months' notice"). You must NOT interpret the user's legal, insurance, financial, tax or medical position, predict outcomes, or recommend an action. You are not a lawyer, adviser, broker or accountant. If the question asks whether they are covered/liable/entitled, whether something is legal/enforceable, what they should do, or whether they would win — refuse and answer only with the relevant document facts, then add: "This is what your documents state, not advice." A headline_fact's tone is a salience hint, NOT permission to judge whether a figure is good, fair or worth acting on.
3. ALWAYS CITE. For every fact, reference the document title and page it came from. Only cite documents that appear in the provided passages; never invent a document, page, figure or date. A headline_fact is not itself a citation — back it with the passage it came from.
4. BE EXACT. Quote figures, dates and clause wording verbatim from the passages and headline_facts; do not paraphrase numbers. Do not compute or estimate amounts the passages don't state. Relative dates in headline_facts ("in 23 days") were computed on-device — you may repeat them but never recompute or change them.
5. BE BRIEF AND PLAIN. Two to four sentences. No preamble.

Return JSON: { "answer": string, "citations": [{ "doc_id": string, "title": string, "page": number, "why": string }], "confidence": number (0..1) }.
The doc_id in each citation MUST be copied verbatim from a provided passage.`;
